import { useEffect, useRef } from 'react';
import { apiRequest } from '@/lib/queryClient';
import { NotificationPlatform } from '@/lib/notifications';

function getClientPlatform(): NotificationPlatform {
  try {
    const Capacitor = typeof window !== 'undefined' ? (window as any).Capacitor : null;
    if (!Capacitor || !Capacitor.isNativePlatform?.()) return 'web';
    const p = Capacitor.getPlatform?.();
    return p === 'ios' ? 'ios' : 'android';
  } catch {
    return 'web';
  }
}

const TOKEN_REFRESH_INTERVAL = 6 * 24 * 60 * 60 * 1000; // 6 days (before 7 days expiration)
const CHECK_INTERVAL = 60 * 60 * 1000; // Check every hour

function safeGetLocalStorage(key: string): string | null {
  try {
    if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
      return null;
    }
    return localStorage.getItem(key);
  } catch (error) {
    console.warn(`Failed to access localStorage for key "${key}":`, error);
    return null;
  }
}

function safeSetLocalStorage(key: string, value: string): void {
  try {
    if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
      return;
    }
    localStorage.setItem(key, value);
  } catch (error) {
    console.warn(`Failed to set localStorage for key "${key}":`, error);
  }
}

export function useTokenRefresh() {
  const refreshTimerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    // Check if user is admin - admins don't need token refresh
    const isAdmin = safeGetLocalStorage('isAdmin') === 'true';
    if (isAdmin) {
      return; // Don't set up token refresh for admin users
    }

    const refreshToken = async () => {
      const token = safeGetLocalStorage('authToken');
      if (!token) return;

      try {
        console.log('🔄 Refreshing authentication token...');
        const response = await apiRequest('POST', '/api/auth/refresh-token');
        const data = await response.json();

        if (data.token) {
          safeSetLocalStorage('authToken', data.token);
          console.log('✅ Token refreshed successfully');
        }
      } catch (error) {
        console.error('❌ Token refresh failed:', error);
        // The 401 handler will automatically log out the user
      }
    };

    const checkAndRefreshToken = () => {
      const token = safeGetLocalStorage('authToken');
      if (!token) return;

      // Check if it's an admin token - don't refresh admin tokens
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        if (payload.isAdmin === true) {
          return; // Don't refresh admin tokens
        }
      } catch (error) {
        // If we can't decode, continue with normal flow
      }

      // Decode JWT to check expiration (simplified)
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        const expiresAt = payload.exp * 1000; // Convert to milliseconds
        const now = Date.now();
        const timeUntilExpiry = expiresAt - now;

        // If token expires in less than 1 day, refresh it
        if (timeUntilExpiry < 24 * 60 * 60 * 1000) {
          console.log(`⏰ Token expires in ${Math.round(timeUntilExpiry / 1000 / 60 / 60)} hours, refreshing...`);
          refreshToken();
        }
      } catch (error) {
        console.error('Error checking token expiration:', error);
      }
    };

    // Ping on app open to track last_login_at
    const pingToken = safeGetLocalStorage('authToken');
    if (pingToken) {
      apiRequest('POST', '/api/auth/ping', { platform: getClientPlatform() }).catch(() => {});
    }

    // Check token expiration every hour
    refreshTimerRef.current = setInterval(checkAndRefreshToken, CHECK_INTERVAL);

    // Initial check
    checkAndRefreshToken();

    return () => {
      if (refreshTimerRef.current) {
        clearInterval(refreshTimerRef.current);
      }
    };
  }, []);
}

